import React from 'react'
import { motion } from 'framer-motion'
import { Frown, Meh, Smile, Laugh, Heart, Sparkles } from 'lucide-react'

interface MoodSelectorProps {
  onMoodSelect: (mood: number) => void
}

const moods = [
  { 
    value: 1, 
    label: 'Molto Male', 
    emoji: '😢',
    icon: Frown, 
    color: 'bg-gradient-to-br from-red-100 to-red-200 hover:from-red-200 hover:to-red-300 border-red-200', 
    iconColor: 'text-red-600',
    description: 'Oggi è stata dura'
  },
  { 
    value: 2, 
    label: 'Male', 
    emoji: '😕',
    icon: Frown, 
    color: 'bg-gradient-to-br from-orange-100 to-orange-200 hover:from-orange-200 hover:to-orange-300 border-orange-200', 
    iconColor: 'text-orange-600',
    description: 'Non il massimo'
  },
  { 
    value: 3, 
    label: 'Neutro', 
    emoji: '😐',
    icon: Meh, 
    color: 'bg-gradient-to-br from-yellow-100 to-yellow-200 hover:from-yellow-200 hover:to-yellow-300 border-yellow-200', 
    iconColor: 'text-yellow-600',
    description: 'Così così'
  },
  { 
    value: 4, 
    label: 'Bene', 
    emoji: '😊',
    icon: Smile, 
    color: 'bg-gradient-to-br from-purple-100 to-purple-200 hover:from-purple-200 hover:to-purple-300 border-purple-200', 
    iconColor: 'text-purple-600',
    description: 'Giornata positiva'
  },
  { 
    value: 5, 
    label: 'Molto Bene', 
    emoji: '🥰',
    icon: Heart, 
    color: 'bg-gradient-to-br from-purple-100 to-purple-200 hover:from-purple-200 hover:to-purple-300 border-purple-200', 
    iconColor: 'text-purple-600',
    description: 'Fantastico!'
  }
]

export default function MoodSelector({ onMoodSelect }: MoodSelectorProps) {
  return (
    <div className="space-y-8">
      <motion.div 
        className="text-center"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-4 shadow-lg"
          animate={{ 
            scale: [1, 1.05, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            duration: 4, 
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <Sparkles className="w-10 h-10 text-purple-600" />
        </motion.div>
        
        <motion.h2 
          className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3"
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          Come ti senti oggi?
        </motion.h2>
        <motion.p 
          className="text-gray-600 text-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Scegli il tuo stato d'animo ✨
        </motion.p>
      </motion.div>

      <div className="space-y-4">
        {moods.map((mood, index) => {
          const IconComponent = mood.icon
          return (
            <motion.button
              key={mood.value}
              onClick={() => onMoodSelect(mood.value)}
              className={`w-full p-5 rounded-2xl border-2 transition-all duration-300 transform shadow-lg hover:shadow-xl ${mood.color}`}
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                scale: 1.02,
                y: -2,
                transition: { duration: 0.2 }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
            >
              <div className="flex items-center space-x-4">
                <motion.div 
                  className="flex-shrink-0 relative"
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-3xl mb-1">{mood.emoji}</div>
                  <motion.div
                    className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full opacity-70"
                    animate={{ scale: [0, 1, 0] }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity,
                      delay: index * 0.3
                    }}
                  />
                </motion.div>
                
                <div className="flex-1 text-left">
                  <h3 className="font-bold text-gray-800 text-lg">
                    {mood.label}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {mood.description}
                  </p>
                </div>
                
                <motion.div 
                  className="text-3xl font-bold text-gray-400"
                  whileHover={{ scale: 1.2 }}
                >
                  {mood.value}
                </motion.div>
              </div>
            </motion.button>
          )
        })}
      </div>

      <motion.div
        className="text-center pt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <p className="text-gray-500 text-sm">
          Ogni momento conta nel tuo viaggio emotivo 💫
        </p>
      </motion.div>
    </div>
  )
}